const GOOGLE_CALENDAR_EVENT_URL_BASE = "https://calendar.google.com/calendar/event?eid=";

function encodeBase64(value) {
  if (typeof globalThis?.btoa === "function") {
    return globalThis.btoa(value);
  }
  if (typeof Buffer !== "undefined") {
    return Buffer.from(value, "utf8").toString("base64");
  }
  return null;
}

export function buildGoogleCalendarEventUrl({ calendarId, eventId } = {}) {
  if (!calendarId || !eventId) {
    return null;
  }

  const encoded = encodeBase64(`${eventId} ${calendarId}`);
  if (!encoded) {
    return null;
  }

  return `${GOOGLE_CALENDAR_EVENT_URL_BASE}${encoded}`;
}

const GOOGLE_CALENDAR_TEMPLATE_URL_BASE =
  "https://calendar.google.com/calendar/render?action=TEMPLATE";

// Identity types whose email is NOT the public identifier — mirrors the server
// attendee-gate inclusion rule (calendar-attendee-gate.js:24). Such users are
// only treated as calendar invitees when they explicitly opt in via
// shareEmailForCalendar.
const EXPLICIT_NON_EMAIL_TYPES = new Set(["qsUsername", "discordUsername"]);

/**
 * Whether a viewer should be shown the "View calendar invite" deep-link for a
 * scheduler. TRUE when the viewer is the creator, when the invited-uids field is
 * missing/non-array (legacy fallback — show), or when the viewer is listed in
 * calendarInvitedUids. A missing viewerUid always yields false.
 */
export function isViewerCalendarInvited(scheduler, viewerUid) {
  if (!viewerUid) {
    return false;
  }
  if (scheduler?.creatorId === viewerUid) {
    return true;
  }
  const invited = scheduler?.calendarInvitedUids;
  if (!Array.isArray(invited)) {
    // Legacy schedulers predate this field — fall back to showing the link.
    return true;
  }
  return invited.includes(viewerUid);
}

/**
 * Normalize a Date / ISO string / epoch-ms / Firestore-Timestamp-like value into
 * a valid Date, or null if it cannot be interpreted.
 */
function normalizeToDate(value) {
  if (value == null) {
    return null;
  }
  let date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "number") {
    date = new Date(value);
  } else if (typeof value === "string") {
    date = new Date(value);
  } else if (typeof value.toDate === "function") {
    date = value.toDate();
  } else if (typeof value.seconds === "number") {
    const nanos = typeof value.nanoseconds === "number" ? value.nanoseconds : 0;
    date = new Date(value.seconds * 1000 + Math.floor(nanos / 1e6));
  } else {
    return null;
  }
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
}

/** Format a Date to UTC "basic" form: yyyyMMddTHHmmssZ. */
function toUtcBasic(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    String(date.getUTCFullYear()).padStart(4, "0") +
    pad(date.getUTCMonth() + 1) +
    pad(date.getUTCDate()) +
    "T" +
    pad(date.getUTCHours()) +
    pad(date.getUTCMinutes()) +
    pad(date.getUTCSeconds()) +
    "Z"
  );
}

/**
 * Build a Google Calendar "TEMPLATE" pre-fill URL so any viewer can add the
 * session to their own calendar without being an attendee. No email/identity is
 * ever encoded. Returns null when title/start/end are missing or unparseable.
 */
export function buildGoogleCalendarTemplateUrl({
  title,
  start,
  end,
  details,
  location,
} = {}) {
  if (!title) {
    return null;
  }
  const startDate = normalizeToDate(start);
  const endDate = normalizeToDate(end);
  if (!startDate || !endDate) {
    return null;
  }

  const params = [
    `text=${encodeURIComponent(title)}`,
    `dates=${toUtcBasic(startDate)}/${toUtcBasic(endDate)}`,
  ];
  if (details) {
    params.push(`details=${encodeURIComponent(details)}`);
  }
  if (location) {
    params.push(`location=${encodeURIComponent(location)}`);
  }

  return `${GOOGLE_CALENDAR_TEMPLATE_URL_BASE}&${params.join("&")}`;
}

/** Escape a value for an ICS text field per RFC 5545. */
function escapeIcsText(value) {
  return String(value)
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r\n|\r|\n/g, "\\n");
}

/**
 * Deterministic, dependency-free hash (djb2). Used to derive a stable ICS UID
 * without Date.now()/Math.random(), which are unavailable in this pure builder.
 */
function stableHash(value) {
  let hash = 5381;
  const str = String(value);
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  // Unsigned base-36 for a compact, deterministic token.
  return (hash >>> 0).toString(36);
}

/**
 * Build a self-contained single-VEVENT .ics as a data: URI so any viewer can
 * download and import the session. Intentionally omits ATTENDEE/ORGANIZER and
 * any email/identity. UID/DTSTAMP are derived deterministically from the input
 * (no wall-clock/random). Returns null when title/start/end are missing or
 * unparseable.
 */
export function buildIcsDataUri({
  title,
  start,
  end,
  details,
  location,
  uid,
} = {}) {
  if (!title) {
    return null;
  }
  const startDate = normalizeToDate(start);
  const endDate = normalizeToDate(end);
  if (!startDate || !endDate) {
    return null;
  }

  const startBasic = toUtcBasic(startDate);
  const endBasic = toUtcBasic(endDate);
  const eventUid =
    uid || `quest-scheduler-${startBasic}-${stableHash(`${title}|${startBasic}`)}`;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Quest Scheduler//Calendar Export//EN",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${escapeIcsText(eventUid)}`,
    `DTSTAMP:${startBasic}`,
    `DTSTART:${startBasic}`,
    `DTEND:${endBasic}`,
    `SUMMARY:${escapeIcsText(title)}`,
  ];
  if (details) {
    lines.push(`DESCRIPTION:${escapeIcsText(details)}`);
  }
  if (location) {
    lines.push(`LOCATION:${escapeIcsText(location)}`);
  }
  lines.push("END:VEVENT", "END:VCALENDAR");

  const ics = lines.join("\r\n");
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

/**
 * Whether a scheduler represents a cancelled session. Parity with the cancelled
 * test in poll-status-meta.jsx (:55-64): true when status is CANCELLED, when
 * calendarSync.state is CANCELLED, or when any of the four cancelledAt sources
 * is set. Pure, zero-API. Shared so the status pill and the calendar control
 * agree on "cancelled".
 */
export function isSessionCancelled(scheduler) {
  if (!scheduler) {
    return false;
  }
  const cancelledAt =
    scheduler.cancelledAt ||
    scheduler.calendarSync?.cancelled?.at ||
    scheduler.calendarSync?.cancelledAt ||
    scheduler.cancelled?.at ||
    null;
  return (
    scheduler.status === "CANCELLED" ||
    scheduler.calendarSync?.state === "CANCELLED" ||
    Boolean(cancelledAt)
  );
}

/**
 * Heuristic "is this viewer on the linked event's guest list?" — NO API call.
 * Mirrors the server attendee-gate inclusion rule (resolveCalendarAttendees in
 * calendar-attendee-gate.js). Used to phrase the duplicate warning and (with a
 * linked calendar) drive the lead-with-Open prominence.
 *   viewer: { publicIdentifierType?, shareEmailForCalendar? } (from useUserSettings)
 * - No googleEventId → false (nothing to be invited to).
 * - Creator → true.
 * - calendarInvitedUids present (authoritative) → membership.
 * - Legacy poll (array absent) → settings heuristic: explicit non-email types
 *   require shareEmailForCalendar === true; email/untyped → true. No uid → false.
 */
export function likelyInvited(scheduler, viewerUid, viewer = {}) {
  if (!scheduler?.googleEventId) {
    return false;
  }
  if (viewerUid && viewerUid === scheduler.creatorId) {
    return true;
  }
  if (Array.isArray(scheduler.calendarInvitedUids)) {
    return Boolean(viewerUid) && scheduler.calendarInvitedUids.includes(viewerUid);
  }
  if (!viewerUid) {
    return false;
  }
  const explicitNonEmail = EXPLICIT_NON_EMAIL_TYPES.has(viewer.publicIdentifierType);
  return explicitNonEmail ? viewer.shareEmailForCalendar === true : true;
}

/**
 * Build the per-surface Add entry ({ title, start, end, details?, location? })
 * from stored scheduler data + the winning slot. Returns null when the entry
 * cannot produce a valid Google-template URL (missing title/slot times), so the
 * caller can suppress Add. `description`/`location` are omitted when absent.
 */
function buildAddEntry(scheduler, slot) {
  if (!scheduler || !slot) {
    return null;
  }
  const entry = { title: scheduler.title, start: slot.start, end: slot.end };
  if (scheduler.description) {
    entry.details = scheduler.description;
  }
  if (scheduler.location) {
    entry.location = scheduler.location;
  }
  // Buildable iff the (identity-free) template URL builds.
  if (!buildGoogleCalendarTemplateUrl(entry)) {
    return null;
  }
  return entry;
}

/**
 * Decide, per surface and per viewer, exactly ONE prominent calendar affordance
 * from stored scheduler data + the viewer's own settings. Pure, zero Google API
 * call.
 *   viewer: { linkedCalendarEmail?, shareEmailForCalendar?, publicIdentifierType? }
 * Returns { mode: "open"|"add"|"none", openUrl, entry, hasLinkedEvent, invited,
 * cancelled }. Lead with Open ONLY when the viewer is on the guest list AND has a
 * linked Google calendar the event could open in; otherwise lead with Add
 * (universal). Cancelled sessions never offer Add — a linked+on-list viewer gets
 * a muted Open to confirm the cancellation; everyone else gets none.
 */
export function resolveSessionCalendarAffordance(scheduler, viewerUid, slot, viewer = {}) {
  const none = {
    mode: "none",
    openUrl: null,
    entry: null,
    hasLinkedEvent: false,
    invited: false,
    cancelled: false,
  };
  if (!scheduler) {
    return none;
  }

  const hasLinkedEvent = Boolean(scheduler.googleEventId);
  const openUrl = hasLinkedEvent
    ? buildGoogleCalendarEventUrl({
        calendarId: scheduler.googleCalendarId || "primary",
        eventId: scheduler.googleEventId,
      })
    : null;
  const invited = likelyInvited(scheduler, viewerUid, viewer);
  const entry = buildAddEntry(scheduler, slot);

  const leadWithOpen =
    hasLinkedEvent && invited && Boolean(viewer.linkedCalendarEmail) && Boolean(openUrl);

  // Cancelled: never Add. A guest-list + linked viewer may still Open (muted) to
  // confirm the cancellation; everyone else gets nothing.
  if (isSessionCancelled(scheduler)) {
    return leadWithOpen
      ? { mode: "open", openUrl, entry: null, hasLinkedEvent, invited, cancelled: true }
      : { ...none, hasLinkedEvent, invited, cancelled: true };
  }

  // Lead with Open: Open prominent; Add moves to the kebab / muted-secondary slot.
  if (leadWithOpen) {
    return { mode: "open", openUrl, entry, hasLinkedEvent, invited, cancelled: false };
  }

  // Otherwise Add is prominent. The dialog still shows a duplicate-warning when a
  // linked event exists (keyed on hasLinkedEvent), but the eid deep-link is
  // exposed ONLY to a likely invitee — the eid encodes the creator's calendarId
  // (often their email), so a non-invitee must not receive it (Model-Y).
  return entry
    ? {
        mode: "add",
        openUrl: invited ? openUrl : null,
        entry,
        hasLinkedEvent,
        invited,
        cancelled: false,
      }
    : { ...none, hasLinkedEvent, invited };
}

